import React, { useEffect, useState, useCallback, useRef } from 'react';
import { useCurrentOrgFprint } from '~/src/entities/user/hooks/useCurrentOrg';
import { http } from '~/src/services/requests';

// Constants
export const TEMPLATE_CONVERSION_SOURCES = {
  DRAFT: 'draft',
  MANAGE: 'manage',
} as const;

export const TEMPLATE_CONVERSION_TYPES = {
  DOCUMENT: 'document',
  TEMPLATE: 'template',
} as const;

// Types
export type TemplateConversionSource =
  (typeof TEMPLATE_CONVERSION_SOURCES)[keyof typeof TEMPLATE_CONVERSION_SOURCES];
export type TemplateConversionType =
  (typeof TEMPLATE_CONVERSION_TYPES)[keyof typeof TEMPLATE_CONVERSION_TYPES];
type FileItem = {
  file: File;
  error: string | false;
  source: TemplateConversionSource;
  type: TemplateConversionType;
};

const useFileUpload = () => {
  const [selectedFiles, setSelectedFiles] = useState<FileItem[]>([]);
  const [isDragOver, setIsDragOver] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const validateFile = (fileItem: FileItem, index: number): FileItem => {
    const isTooLarge = fileItem.file.size >= 5 * 1000 * 1000;
    const isLessThanFive = index > 4;
    const isDocx =
      fileItem.file.name.toLowerCase().endsWith('.docx') ||
      fileItem.file.name.toLowerCase().endsWith('.doc');
    // Also check MIME type for files downloaded from Clio (which have correct MIME type but may not have extension in name)
    const isWordDocument =
      isDocx ||
      fileItem.file.type ===
        'application/vnd.openxmlformats-officedocument.wordprocessingml.document' ||
      fileItem.file.type === 'application/msword';
    const errorMessage = isTooLarge
      ? 'File is too large'
      : isLessThanFive
      ? 'We only support up to 5 files'
      : !isWordDocument
      ? 'We only support Word documents (.docx/.doc files)'
      : false;

    if (errorMessage) {
      return {
        ...fileItem,
        error: errorMessage,
      };
    } else {
      return {
        ...fileItem,
        error: false,
      };
    }
  };

  const createFileItem = (
    file: File,
    index: number,
    source: TemplateConversionSource = TEMPLATE_CONVERSION_SOURCES.DRAFT,
    type: TemplateConversionType = TEMPLATE_CONVERSION_TYPES.DOCUMENT,
  ): FileItem => {
    return validateFile(
      {
        file,
        error: false,
        source,
        type,
      },
      index,
    );
  };

  const isFileAlreadySelected = (
    fileItem: FileItem,
    existingFiles: FileItem[],
  ): boolean => {
    const file = fileItem.file;
    return existingFiles.some(
      ({ file: existingFile }) =>
        existingFile.name === file.name && existingFile.size === file.size,
    );
  };

  const addFiles = (
    files: FileList,
    source: TemplateConversionSource = TEMPLATE_CONVERSION_SOURCES.DRAFT,
    type: TemplateConversionType = TEMPLATE_CONVERSION_TYPES.DOCUMENT,
  ) => {
    const fileItemList = Array.from(files).map((file, index) =>
      createFileItem(file, index, source, type),
    );
    setSelectedFiles((prevFiles) => {
      const newFiles = fileItemList.filter(
        (file) => !isFileAlreadySelected(file, prevFiles),
      );

      return [...prevFiles, ...newFiles].map(validateFile);
    });
  };

  const removeFileAtIndex = (index: number) => {
    setSelectedFiles((prevFiles) => {
      return prevFiles.filter((_, i) => i !== index).map(validateFile);
    });
  };

  const onDragOver = (event: React.DragEvent<HTMLDivElement>) => {
    event.preventDefault();
    event.stopPropagation();
    if (!isDragOver) {
      setIsDragOver(true);
    }
  };

  const onDragEnter = (event: React.DragEvent<HTMLDivElement>) => {
    event.preventDefault();
    event.stopPropagation();
    setIsDragOver(true);
  };

  const onDragLeave = (event: React.DragEvent<HTMLDivElement>) => {
    event.preventDefault();
    event.stopPropagation();
    // Only set isDragOver to false if we're leaving the drop zone entirely
    const rect = event.currentTarget.getBoundingClientRect();
    const x = event.clientX;
    const y = event.clientY;

    if (x < rect.left || x >= rect.right || y < rect.top || y >= rect.bottom) {
      setIsDragOver(false);
    }
  };

  const onDrop = (event: React.DragEvent<HTMLDivElement>) => {
    event.preventDefault();
    event.stopPropagation();
    setIsDragOver(false);

    addFiles(
      event.dataTransfer.files,
      TEMPLATE_CONVERSION_SOURCES.DRAFT,
      TEMPLATE_CONVERSION_TYPES.DOCUMENT,
    );
  };

  const onOpenFilePicker = () => {
    if (fileInputRef.current) {
      fileInputRef.current.click();
    }
  };

  const onResetFileUpload = () => {
    setSelectedFiles([]);
    setIsDragOver(false);
  };

  return {
    selectedFiles,
    isDragOver,
    fileInputRef,
    addFiles,
    removeFileAtIndex,
    onDragOver,
    onDragEnter,
    onDragLeave,
    onDrop,
    onOpenFilePicker,
    onResetFileUpload,
  };
};

const getJobDetails = async (orgFprint: string, jobFprint: string) => {
  const response = await http.innerSendAuthenticated(
    {
      relativePath: `/document/api/v1/org/${orgFprint}/template/convert/job/${jobFprint}/`,
      method: 'GET',
    },
    true,
  );

  return response;
};

const getActiveJobs = async (orgFprint: string) => {
  const response = await http.innerSendAuthenticated(
    {
      relativePath: `/document/api/v1/org/${orgFprint}/template/convert/jobs/?status=PENDING,PROCESSING`,
      method: 'GET',
    },
    true,
  );

  return response;
};

const getPresignedUploadUrls = async (
  orgFprint: string,
  filenames: string[],
  source: TemplateConversionSource = TEMPLATE_CONVERSION_SOURCES.DRAFT,
) => {
  const response = await http.innerSendAuthenticated(
    {
      relativePath: `/document/api/v1/org/${orgFprint}/template/convert/presigned-upload/`,
      method: 'POST',
      body: {
        filenames,
        source,
      },
    },
    true,
  );

  return response;
};

const uploadFileToS3 = async (
  url: string,
  fields: Record<string, string>,
  file: File,
): Promise<void> => {
  const formData = new FormData();

  // Add all the fields from the presigned POST
  Object.keys(fields).forEach((key) => {
    const value = fields[key];
    if (value !== undefined) {
      formData.append(key, value);
    }
  });

  // Add the file last - browser will automatically set content-type based on file extension
  formData.append('file', file);

  // Upload directly to S3 (no auth needed, using presigned URL)
  const response = await fetch(url, {
    method: 'POST',
    body: formData,
  });

  if (!response.ok) {
    throw new Error(
      `S3 upload failed: ${response.status} ${response.statusText}`,
    );
  }
};

const finalizeUpload = async (
  orgFprint: string,
  jobFprint: string,
  files: {
    filename: string;
    s3_key: string;
    source: TemplateConversionSource;
    type: TemplateConversionType;
  }[],
) => {
  const response = await http.innerSendAuthenticated(
    {
      relativePath: `/document/api/v1/org/${orgFprint}/template/convert/finalize-upload/`,
      method: 'POST',
      body: {
        job_fprint: jobFprint,
        files,
      },
    },
    true,
  );

  return response;
};

const useTemplateConverterJob = () => {
  const [jobOrgFprint, setJobOrgFprint] = useState<string | null>(null);
  const [jobFprint, setJobFprint] = useState<string | null>(null);
  const [jobStatus, setJobStatus] = useState<string | null>(null);
  const [jobError, setJobError] = useState<string | null>(null);
  const [jobData, setJobData] = useState<any>(null);

  const orgFprint = useCurrentOrgFprint();

  const pollingIntervalRef = React.useRef<ReturnType<
    typeof setInterval
  > | null>(null);
  // Consistent cleanup helper
  const clearPollingInterval = useCallback(() => {
    if (pollingIntervalRef.current) {
      clearInterval(pollingIntervalRef.current);
      pollingIntervalRef.current = null;
    }
  }, []);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      clearPollingInterval();
    };
  }, [clearPollingInterval]);

  const checkForActiveJobs = useCallback(async () => {
    if (!orgFprint || jobFprint) {
      return;
    }

    try {
      const response = await getActiveJobs(orgFprint);

      if (response?.jobs?.length > 0) {
        const activeJob = response.jobs[0];
        setJobFprint(activeJob.job_fprint);
        setJobOrgFprint(orgFprint);
        setJobStatus(activeJob.status.toLowerCase());
        setJobError(null);
      }
    } catch {
      // Silently fail - best-effort attempt to restore user's active job state
    }
  }, [
    orgFprint,
    jobFprint,
    setJobFprint,
    setJobOrgFprint,
    setJobStatus,
    setJobError,
  ]);

  useEffect(() => {
    checkForActiveJobs();
  }, [checkForActiveJobs]);

  const createJob = async (
    files: File[],
    source: TemplateConversionSource = TEMPLATE_CONVERSION_SOURCES.DRAFT,
    type: TemplateConversionType = TEMPLATE_CONVERSION_TYPES.DOCUMENT,
  ) => {
    if (!orgFprint || !files.length) {
      throw new Error('Invalid parameters');
    }

    clearPollingInterval();
    setJobError(null);
    setJobFprint(null);
    setJobOrgFprint(null);
    setJobStatus('pending');

    try {
      const filenames = files.map((f) => f.name);
      const presignedResponse = await getPresignedUploadUrls(
        orgFprint,
        filenames,
        source,
      );

      if (!presignedResponse || !presignedResponse.job_fprint) {
        throw new Error('Failed to get upload URLs');
      }

      const { job_fprint: jobFprint, uploads } = presignedResponse;

      if (uploads.length !== files.length) {
        throw new Error('Upload URLs do not match selected files');
      }

      const uploadResults = await Promise.all(
        uploads.map(async (uploadInfo: any, index: number) => {
          const file = files[index];
          if (!file) {
            throw new Error(`File at index ${index} is missing`);
          }
          try {
            await uploadFileToS3(uploadInfo.url, uploadInfo.fields, file);
            return { uploadInfo, success: true };
          } catch (error) {
            console.error(`Failed to upload ${file.name}:`, error);
            return { uploadInfo, success: false };
          }
        }),
      );

      const finalizeFiles = uploadResults.map((result) => ({
        filename: result.uploadInfo.filename,
        s3_key: result.uploadInfo.s3_key,
        source,
        type,
        upload_succeeded: result.success,
      }));

      const finalizeResponse = await finalizeUpload(
        orgFprint,
        jobFprint,
        finalizeFiles,
      );

      setJobOrgFprint(orgFprint);
      setJobFprint(jobFprint);

      if (finalizeResponse.processing_started) {
        setJobStatus('pending');
      } else {
        setJobStatus('failed');
        setJobError('All files failed validation');
      }
    } catch (error: any) {
      setJobStatus('failed');
      setJobError(error.message || 'Failed to create job');
    }
  };

  const pollJobStatus = useCallback(async () => {
    if (!jobOrgFprint || !jobFprint) {
      return;
    }
    try {
      const response = await getJobDetails(jobOrgFprint, jobFprint);

      if (response && response.status) {
        const status = response.status.toLowerCase();
        setJobStatus(status);
        setJobData(response);

        if (status === 'completed') {
          setJobError(null);
          clearPollingInterval();
        } else if (status === 'failed') {
          setJobError('Conversion job failed');
          clearPollingInterval();
        }
      } else {
        setJobError('Invalid job response');
        setJobStatus(null);
        clearPollingInterval();
      }
    } catch (error: any) {
      setJobError(error.message || 'Failed to get job status');
      setJobStatus(null);
      clearPollingInterval();
    }
  }, [jobOrgFprint, jobFprint, clearPollingInterval]);

  useEffect(() => {
    if (!jobOrgFprint || !jobFprint) {
      return;
    }

    // Cancel polling if user switched organizations
    if (jobOrgFprint !== orgFprint) {
      clearPollingInterval();
      setJobFprint(null);
      setJobOrgFprint(null);
      setJobStatus(null);
      return;
    }

    pollJobStatus(); // Initial call
    pollingIntervalRef.current = setInterval(pollJobStatus, 2000);
    return () => {
      clearPollingInterval();
    };
  }, [jobFprint, jobOrgFprint, pollJobStatus, orgFprint, clearPollingInterval]);

  const resetJob = () => {
    setJobFprint(null);
    setJobOrgFprint(null);
    setJobStatus(null);
    setJobError(null);
    setJobData(null);
  };

  return {
    jobFprint,
    jobStatus,
    jobError,
    jobData,
    createJob,
    setJobStatus,
    resetJob,
  };
};

export const useTemplateConverter = () => {
  const [convertError, setConvertError] = useState<string | null>(null);

  const {
    selectedFiles,
    isDragOver,
    fileInputRef,
    addFiles,
    removeFileAtIndex,
    onDragOver,
    onDragEnter,
    onDragLeave,
    onDrop,
    onOpenFilePicker,
    onResetFileUpload,
  } = useFileUpload();

  const {
    jobFprint,
    jobStatus,
    jobError,
    jobData,
    createJob,
    setJobStatus,
    resetJob,
  } = useTemplateConverterJob();

  const handleFileUpload = (event: React.ChangeEvent<HTMLInputElement>) => {
    const files = event.target.files;
    if (files && files.length > 0) {
      addFiles(
        files,
        TEMPLATE_CONVERSION_SOURCES.DRAFT,
        TEMPLATE_CONVERSION_TYPES.DOCUMENT,
      );
      setConvertError(null);
    }
    // Clear the input value so the same file can be selected again
    event.target.value = '';
  };

  const handleConvertClick = async () => {
    try {
      setJobStatus('pending');
      const files = selectedFiles.map((fileItem) => fileItem.file);
      const source =
        selectedFiles[0]?.source || TEMPLATE_CONVERSION_SOURCES.DRAFT;
      const type = selectedFiles[0]?.type || TEMPLATE_CONVERSION_TYPES.DOCUMENT;
      await createJob(files, source, type);
    } catch (error: any) {
      setConvertError(error.message || 'Failed to convert template');
    }
  };

  const handleReset = () => {
    setConvertError(null);
    onResetFileUpload();
    resetJob();
  };

  return {
    // State
    selectedFiles,
    convertError,
    isDragOver,
    jobStatus,
    jobFprint,
    jobError,
    jobData,
    fileInputRef,

    // Actions
    handleReset,
    handleFileUpload,
    handleConvertClick,
    openFilePicker: onOpenFilePicker,
    handleRemoveFile: removeFileAtIndex,
    handleDragOver: onDragOver,
    handleDragEnter: onDragEnter,
    handleDragLeave: onDragLeave,
    handleDrop: onDrop,
    addFiles,
  };
};

export type { FileItem };
